import { useCallback, useContext, useEffect, useId, useMemo } from 'react'
import CurrencyContext from 'contexts/currencyContext'
import submitLoyaltyPointsBurnCalculationRequest from 'actions/loyalty/submitLoyaltyPointsBurnCalculationRequest'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OfferPageBurnSelectionContext } from 'contexts/OfferPage/OfferPageBurnSelectionContext'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import buildBurnKey from 'loyalty/lib/pointsCalculation/buildBurnKey'
import type { BurnBatchRequest } from 'api/loyalty/requestLoyaltyPointsBurnCalculationBatch'
import useLuxPlusDisplayedMode from 'luxPlus/hooks/useLuxPlusDisplayedMode'
import getLuxPlusPrimaryCashPrice from 'luxPlus/lib/getLuxPlusPrimaryCashPrice'
import { isGoogleHotelAdsBot } from 'selectors/accountSelectors'
import { isGhaPriceDisplay } from 'selectors/routerSelectors'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import { MINIMUM_POINTS_TO_APPLY } from 'loyalty/constants/loyaltyBurn'
import { getPackageSelectionBurnQuote, type PackageSelectionBurnItem } from './packageSelectionBurnQuote'

interface Options {
  /** Only the section owning the actual selection registers the slider maximum. */
  registerMaximum?: boolean
  disabled?: boolean
}

function usePackageSelectionBurnQuote(items: ReadonlyArray<PackageSelectionBurnItem>, { registerMaximum = false, disabled = false }: Options = {}) {
  const dispatch = useAppDispatch()
  const { pointsToApply, charterBundleEnabled, offer } = useContext(OfferPageStateContext)
  const { setQuote } = useContext(OfferPageBurnSelectionContext)
  const quoteOwnerId = useId()
  const burnSelected = useAppSelector(state => state.loyalty.hasSelectedBurnToggle)
  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const isLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const enabled = burnSelected && loyaltyEnabled && isLoyaltyRegion && !charterBundleEnabled && !disabled
  const customerId = useAppSelector(state => state.auth.account.memberId ?? '')
  const currency = useContext(CurrencyContext)
  const availablePoints = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const displayedMode = useLuxPlusDisplayedMode({
    price: items.reduce((total, item) => total + item.price, 0),
    memberPrice: items.reduce((total, item) => total + item.memberPrice, 0),
    offerConfig: offer?.luxPlus,
  })
  const isGoogleHotelAds = useAppSelector(isGoogleHotelAdsBot)
  const isGhaPricing = useAppSelector(isGhaPriceDisplay)
  const requests = useMemo(() => {
    const unique = new Map<string, BurnBatchRequest>()
    for (const item of items) {
      for (const price of [item.price, item.memberPrice]) {
        if (price <= 0) continue
        const request = { price, loyaltyProductType: item.loyaltyProductType, mx: item.mx, currency }
        unique.set(buildBurnKey(request, customerId), request)
      }
    }
    return Array.from(unique.values())
  }, [currency, customerId, items])
  const requestKeys = useMemo(() => requests.map(request => buildBurnKey(request, customerId)), [customerId, requests])
  const requestedCalculations = useAppSelector(useCallback((state: App.State) => requestKeys.map(key => state.loyalty.pointsBurnBatchCalculations?.[key]), [requestKeys]), shallowEqual)
  const calculations = useMemo(() => new Map(requestKeys.map((key, index) => [key, requestedCalculations[index]])), [requestKeys, requestedCalculations])
  useEffect(() => {
    if (!enabled) return
    requests.forEach((request, index) => {
      const entry = requestedCalculations[index]
      if (!entry?.data && !entry?.fetching && !entry?.error) dispatch(submitLoyaltyPointsBurnCalculationRequest(request))
    })
  }, [dispatch, enabled, requestedCalculations, requests])
  const quote = useMemo(() => {
    if (!enabled || !items.length) return undefined
    const allowance = Math.min(pointsToApply ?? availablePoints, availablePoints)
    return getPackageSelectionBurnQuote(items, (item, price) => calculations.get(buildBurnKey({
      price, loyaltyProductType: item.loyaltyProductType, mx: item.mx, currency,
    }, customerId))?.data, allowance >= MINIMUM_POINTS_TO_APPLY ? allowance : 0)
  }, [availablePoints, calculations, currency, customerId, enabled, items, pointsToApply])
  const fetching = enabled && items.length > 0 && !quote && requests.some(request => {
    const entry = calculations.get(buildBurnKey(request, customerId))
    return !entry?.data && !entry?.error
  })
  const maximum = quote && getLuxPlusPrimaryCashPrice(quote.regularMaximum, quote.memberMaximum, displayedMode, isGoogleHotelAds, isGhaPricing)
  const failed = enabled && !quote && requestedCalculations.some(entry => !!entry?.error)
  const ready = !enabled || !items.length || !!quote
  useEffect(() => {
    if (!registerMaximum) return
    setQuote(enabled ? { maximum, fetching, failed, ready } : undefined, quoteOwnerId)
  }, [enabled, failed, fetching, maximum, quoteOwnerId, ready, registerMaximum, setQuote])
  useEffect(() => {
    if (!registerMaximum) return
    return () => setQuote(undefined, quoteOwnerId)
  }, [quoteOwnerId, registerMaximum, setQuote])
  return { enabled, fetching, quote, ready }
}

export default usePackageSelectionBurnQuote
